import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Calendar, FileText, TrendingUp, DollarSign, Download } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { apiService } from '../../services/apiService'

const FinancialStatements = () => {
  const { customerId } = useParams()
  const { admin } = useAdminAuth()
  const [customer, setCustomer] = useState(null)
  const [statements, setStatements] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedStatement, setSelectedStatement] = useState(null)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    fetchCustomerData()
    fetchFinancialStatements()
  }, [customerId])

  const fetchCustomerData = async () => {
    try {
      const response = await apiService.getCustomerDetails(customerId)
      
      if (response.success) {
        setCustomer(response.data)
      } else {
        // Mock customer data
        setCustomer({
          id: customerId,
          client_id: 'CL001',
          username: 'john_kamau',
          email: 'john@example.com',
          phone_number: '+256700123456',
          status: 'active',
          registration_date: '2024-01-15',
          last_login: '2024-03-15'
        })
      }
    } catch (error) {
      console.error('Failed to fetch customer data:', error)
    }
  }

  const fetchFinancialStatements = async () => {
    try {
      const response = await apiService.getCustomerFinancialStatements(customerId)
      
      if (response.success) {
        setStatements(response.data || [])
      } else {
        // Mock financial statements data
        setStatements([
          {
            id: 1,
            type: 'profit_loss',
            submission_date: '2024-03-15',
            period_start: '2024-01-01',
            period_end: '2024-03-31',
            status: 'approved',
            data: {
              revenue: 2500000,
              cost_of_goods_sold: 1200000,
              gross_profit: 1300000,
              operating_expenses: 800000,
              operating_income: 500000,
              other_income: 50000,
              other_expenses: 30000,
              net_income: 520000
            }
          },
          {
            id: 2,
            type: 'balance_sheet',
            submission_date: '2024-03-15',
            as_of_date: '2024-03-31',
            status: 'pending',
            data: {
              total_assets: 5000000,
              cash_and_equivalents: 800000,
              accounts_receivable: 1200000,
              inventory: 2000000,
              fixed_assets: 1000000,
              total_liabilities: 2500000,
              accounts_payable: 500000,
              short_term_debt: 800000,
              long_term_debt: 1200000,
              owner_equity: 1500000,
              retained_earnings: 1000000,
              total_equity: 2500000
            }
          },
          {
            id: 3,
            type: 'profit_loss',
            submission_date: '2024-02-15',
            period_start: '2024-01-01',
            period_end: '2024-02-28',
            status: 'approved',
            data: {
              revenue: 2200000,
              cost_of_goods_sold: 1100000,
              gross_profit: 1100000,
              operating_expenses: 750000,
              operating_income: 350000,
              other_income: 25000,
              other_expenses: 15000,
              net_income: 360000
            }
          }
        ])
      }
    } catch (error) {
      console.error('Failed to fetch financial statements:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success-100 text-success-800'
      case 'pending':
        return 'bg-warning-100 text-warning-800'
      case 'rejected':
        return 'bg-danger-100 text-danger-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const renderProfitLossStatement = (statement) => {
    const data = statement.data
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Revenue & Costs</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Revenue</span>
                <span className="font-medium">{formatCurrency(data.revenue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cost of Goods Sold</span>
                <span className="font-medium text-red-600">({formatCurrency(data.cost_of_goods_sold)})</span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between font-semibold">
                  <span>Gross Profit</span>
                  <span className="text-green-600">{formatCurrency(data.gross_profit)}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Operating Performance</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Operating Expenses</span>
                <span className="font-medium text-red-600">({formatCurrency(data.operating_expenses)})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Operating Income</span>
                <span className="font-medium">{formatCurrency(data.operating_income)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Other Income</span>
                <span className="font-medium text-green-600">{formatCurrency(data.other_income)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Other Expenses</span>
                <span className="font-medium text-red-600">({formatCurrency(data.other_expenses)})</span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Net Income</span>
                  <span className={data.net_income >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {formatCurrency(data.net_income)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderBalanceSheet = (statement) => {
    const data = statement.data
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Assets</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Cash & Equivalents</span>
                <span className="font-medium">{formatCurrency(data.cash_and_equivalents)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Accounts Receivable</span>
                <span className="font-medium">{formatCurrency(data.accounts_receivable)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Inventory</span>
                <span className="font-medium">{formatCurrency(data.inventory)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fixed Assets</span>
                <span className="font-medium">{formatCurrency(data.fixed_assets)}</span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between font-semibold">
                  <span>Total Assets</span>
                  <span>{formatCurrency(data.total_assets)}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Liabilities</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Accounts Payable</span>
                <span className="font-medium">{formatCurrency(data.accounts_payable)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Short-term Debt</span>
                <span className="font-medium">{formatCurrency(data.short_term_debt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Long-term Debt</span>
                <span className="font-medium">{formatCurrency(data.long_term_debt)}</span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between font-semibold">
                  <span>Total Liabilities</span>
                  <span>{formatCurrency(data.total_liabilities)}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Equity</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Owner's Equity</span>
                <span className="font-medium">{formatCurrency(data.owner_equity)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Retained Earnings</span>
                <span className="font-medium">{formatCurrency(data.retained_earnings)}</span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between font-semibold">
                  <span>Total Equity</span>
                  <span>{formatCurrency(data.total_equity)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Balance Check */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">Balance Check:</span>
            <span className={
              data.total_assets === (data.total_liabilities + data.total_equity) 
                ? 'text-green-600 font-semibold' 
                : 'text-red-600 font-semibold'
            }>
              {data.total_assets === (data.total_liabilities + data.total_equity) 
                ? '✓ Balanced' 
                : '✗ Not Balanced'
              }
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Assets: {formatCurrency(data.total_assets)} = 
            Liabilities: {formatCurrency(data.total_liabilities)} + 
            Equity: {formatCurrency(data.total_equity)} = 
            {formatCurrency(data.total_liabilities + data.total_equity)}
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Link
              to="/admin/customers"
              className="btn btn-secondary"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Customers
            </Link>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Financial Statements
              </h1>
              <p className="text-gray-600 mt-2">
                {customer?.username} (Client ID: {customer?.client_id})
              </p>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        {customer && (
          <div className="card card-body mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <h3 className="font-medium text-gray-900">Customer Information</h3>
                <div className="mt-2 space-y-1">
                  <p className="text-sm text-gray-600">Username: {customer.username}</p>
                  <p className="text-sm text-gray-600">Client ID: {customer.client_id}</p>
                  <p className="text-sm text-gray-600">Email: {customer.email || 'N/A'}</p>
                  <p className="text-sm text-gray-600">Phone: {customer.phone_number || 'N/A'}</p>
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Account Status</h3>
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    customer.status === 'active' ? 'bg-success-100 text-success-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {customer.status?.charAt(0).toUpperCase() + customer.status?.slice(1)}
                  </span>
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Registration</h3>
                <p className="text-sm text-gray-600 mt-2">
                  {customer.registration_date ? new Date(customer.registration_date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Last Login</h3>
                <p className="text-sm text-gray-600 mt-2">
                  {customer.last_login ? new Date(customer.last_login).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Statements List */}
          <div className="lg:col-span-1">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Submitted Statements</h3>
              </div>
              <div className="card-body p-0">
                {statements.length > 0 ? (
                  <div className="space-y-2">
                    {statements.map((statement) => (
                      <div
                        key={statement.id}
                        className={`p-4 border-l-4 cursor-pointer hover:bg-gray-50 ${
                          selectedStatement?.id === statement.id 
                            ? 'bg-primary-50 border-primary-500' 
                            : 'border-gray-200'
                        }`}
                        onClick={() => setSelectedStatement(statement)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center space-x-2">
                              <FileText className="h-5 w-5 text-gray-400" />
                              <span className="font-medium text-gray-900">
                                {statement.type === 'profit_loss' ? 'P&L Statement' : 'Balance Sheet'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              Submitted: {new Date(statement.submission_date).toLocaleDateString()}
                            </p>
                            <p className="text-sm text-gray-600">
                              Period: {statement.type === 'profit_loss' 
                                ? `${new Date(statement.period_start).toLocaleDateString()} - ${new Date(statement.period_end).toLocaleDateString()}`
                                : `As of ${new Date(statement.as_of_date).toLocaleDateString()}`
                              }
                            </p>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(statement.status)}`}>
                            {statement.status.charAt(0).toUpperCase() + statement.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No financial statements submitted</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Statement Details */}
          <div className="lg:col-span-2">
            {selectedStatement ? (
              <div className="card">
                <div className="card-header">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedStatement.type === 'profit_loss' ? 'Profit & Loss Statement' : 'Balance Sheet'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Submitted on {new Date(selectedStatement.submission_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedStatement.status)}`}>
                        {selectedStatement.status.charAt(0).toUpperCase() + selectedStatement.status.slice(1)}
                      </span>
                      <button className="btn btn-secondary btn-sm">
                        <Download className="h-4 w-4 mr-1" />
                        Export
                      </button>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {selectedStatement.type === 'profit_loss' 
                    ? renderProfitLossStatement(selectedStatement)
                    : renderBalanceSheet(selectedStatement)
                  }
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-body text-center py-12">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Select a Statement
                  </h3>
                  <p className="text-gray-600">
                    Choose a financial statement from the list to view its details
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Summary Statistics */}
        {statements.length > 0 && (
          <div className="mt-8 bg-primary-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Statement Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary-600">{statements.length}</p>
                <p className="text-sm text-gray-600">Total Statements</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-success-600">
                  {statements.filter(s => s.status === 'approved').length}
                </p>
                <p className="text-sm text-gray-600">Approved</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-warning-600">
                  {statements.filter(s => s.status === 'pending').length}
                </p>
                <p className="text-sm text-gray-600">Pending Review</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-danger-600">
                  {statements.filter(s => s.status === 'rejected').length}
                </p>
                <p className="text-sm text-gray-600">Rejected</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FinancialStatements
